import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/program-definitions',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/benefit-programs-program-definitions',
        destPath: '',
      },
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
      {
        srcPath: '/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
    '*.pub.wix-code.com': [
      {
        srcPath: '/_api/benefit-programs/v1/program-definitions',
        destPath: '/v1/program-definitions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_benefit-programs_program-definitions';

/** Creates a program definition. */
export function createProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __createProgramDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'programDefinition.createdDate' },
          { path: 'programDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.CreateProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        { protoPath: '/v1/program-definitions', data: serializedData, host }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programDefinition.createdDate' },
              { path: 'programDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createProgramDefinition;
}

/**
 * Updates a program definition.
 *
 * Each time the program definition is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the program definition.
 * This ensures you're working with the latest program definition
 * and prevents unintended overwrites.
 */
export function updateProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __updateProgramDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'programDefinition.createdDate' },
          { path: 'programDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'PATCH' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.UpdateProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        {
          protoPath: '/v1/program-definitions/{programDefinition.id}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programDefinition.createdDate' },
              { path: 'programDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateProgramDefinition;
}

/** Deletes a program definition. */
export function deleteProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteProgramDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'DELETE' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.DeleteProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        {
          protoPath: '/v1/program-definitions/{programDefinitionId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteProgramDefinition;
}

/** Retrieves a program definition. */
export function getProgramDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __getProgramDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'GET' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.GetProgramDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        {
          protoPath: '/v1/program-definitions/{programDefinitionId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programDefinition.createdDate' },
              { path: 'programDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getProgramDefinition;
}

/** Retrieves a program definition with the specified external ID and namespace. */
export function getProgramDefinitionByExternalIdAndNamespace(
  payload: object
): RequestOptionsFactory<any> {
  function __getProgramDefinitionByExternalIdAndNamespace({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'GET' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.GetProgramDefinitionByExternalIdAndNamespace',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        {
          protoPath: '/v1/program-definitions/by-namespace-and-external-id',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programDefinition.createdDate' },
              { path: 'programDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getProgramDefinitionByExternalIdAndNamespace;
}

/**
 * Creates a query to retrieve a list of program definitions.
 *
 * The Query Program Definitions method builds a query to retrieve a list of program definitions and returns a `ProgramDefinitionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `ProgramDefinitionsQueryBuilder` methods onto the query. `ProgramDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Program Definitions returns.
 *
 * Query Program Definitions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `ProgramDefinitionsQueryResult`.
 */
export function queryProgramDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryProgramDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.benefit_programs.v1.program_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.benefit_programs.v1.program_definition.ProgramDefinitionService.QueryProgramDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBenefitProgramsV1ProgramDefinitionProgramDefinitionServiceUrl(
        { protoPath: '/v1/program-definitions/query', data: payload, host }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'programDefinitions.createdDate' },
              { path: 'programDefinitions.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryProgramDefinitions;
}
